import { FormEvent, useMemo, useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import Modal from "../ui/Modal";
import ConfirmDeleteModal from "../modals/ConfirmDeleteModal";
import { deleteParsingSettings, saveParsingSettings } from "../../api/parsing";
import { AdminHotelRow, ParsingTask } from "../../types/hotels";

const DEPTH_OPTIONS = [15, 30, 45, 60, 90, 120, 160, 180, 360];
const INTERVAL_OPTIONS = [
  { value: 1, label: "Один раз в месяц" },
  { value: 4, label: "Один раз в неделю" },
  { value: 30, label: "Один раз в день" },
  { value: 60, label: "Два раза в день" },
  { value: 90, label: "Три раза в день" },
  { value: 120, label: "Четыре раза в день" },
];
const GUEST_OPTIONS = [1, 2, 3, 4];

type Props = {
  open: boolean;
  hotel: AdminHotelRow | null;
  onClose: () => void;
  onRefresh: () => Promise<void>;
};

function getApiErrorMessage(error: any, fallback: string) {
  const apiError =
    error?.response?.data?.error ??
    error?.response?.data?.detail ??
    error?.response?.data?.message ??
    error?.error ??
    error?.message;

  if (typeof apiError === "string" && apiError.trim()) return apiError;
  if (apiError != null) {
    try {
      return JSON.stringify(apiError);
    } catch {
      return String(apiError);
    }
  }
  return fallback;
}

function formatDate(value: string | null) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("ru-RU");
}

function getTaskKey(task: ParsingTask, index: number) {
  return task.task_id ?? `${task.min_stay}-${task.depth}-${task.interval}-${task.guests}-${index}`;
}

function formatInterval(interval: number) {
  return INTERVAL_OPTIONS.find((option) => option.value === interval)?.label ?? String(interval);
}

export default function ParsingSettingsModal({ open, hotel, onClose, onRefresh }: Props) {
  const [formOpen, setFormOpen] = useState(false);
  const [minStay, setMinStay] = useState(1);
  const [depth, setDepth] = useState(90);
  const [interval, setInterval] = useState(4);
  const [guests, setGuests] = useState(2);
  const [startNow, setStartNow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [deletingTask, setDeletingTask] = useState<ParsingTask | null>(null);

  const parsing = useMemo(() => hotel?.parsing ?? [], [hotel?.parsing]);
  const canEdit = Boolean(hotel?.group_id);

  function resetForm() {
    setMinStay(1);
    setDepth(90);
    setInterval(4);
    setGuests(2);
    setStartNow(false);
    setFormError("");
  }

  function handleClose() {
    setFormOpen(false);
    resetForm();
    onClose();
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!hotel?.group_id) {
      setFormError("Для объекта не задана группа парсинга");
      return;
    }
    if (!Number.isInteger(minStay) || minStay < 1 || minStay > 10) {
      setFormError("min_stay должен быть целым числом от 1 до 10");
      return;
    }

    setSaving(true);
    setFormError("");
    try {
      const response = await saveParsingSettings({
        group_id: hotel.group_id,
        min_stay: minStay,
        depth,
        interval,
        guests,
        start_now: startNow ? 1 : 0,
      });
      if (response.status !== "ok") {
        throw new Error(getApiErrorMessage(response, "Ошибка сохранения парсинга"));
      }
      setFormOpen(false);
      resetForm();
      await onRefresh();
    } catch (error: any) {
      setFormError(getApiErrorMessage(error, "Ошибка сохранения парсинга"));
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteConfirm() {
    if (!hotel?.group_id || !deletingTask?.task_id) {
      throw new Error("Не удалось определить парсинг для удаления");
    }

    const response = await deleteParsingSettings({
      group_id: hotel.group_id,
      task_id: deletingTask.task_id,
    });
    if (response.status !== "ok") {
      throw new Error(getApiErrorMessage(response, "Ошибка удаления парсинга"));
    }
    setFormOpen(false);
    resetForm();
    await onRefresh();
    setDeletingTask(null);
  }

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        title={hotel ? `Парсинг: ${hotel.name}` : "Парсинг"}
        widthClass="max-w-3xl"
      >
        <div className="space-y-5">
          {!canEdit && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              Для объекта не задана группа парсинга
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-slate-900">Настроенные парсинги</div>
              <div className="mt-1 text-sm text-slate-500">
                {parsing.length ? `Всего: ${parsing.length}` : "Парсинги не настроены"}
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                setFormOpen((current) => !current);
                setFormError("");
              }}
              disabled={!canEdit || saving}
              className="inline-flex items-center gap-2 rounded-2xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Plus size={16} />
              Добавить парсинг
            </button>
          </div>

          {formOpen && (
            <form onSubmit={handleSubmit} className="space-y-4 rounded-3xl border border-slate-200 bg-slate-50 p-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="space-y-1 text-sm font-semibold text-slate-700">
                  <span>min_stay</span>
                  <input
                    type="number"
                    min={1}
                    max={10}
                    step={1}
                    value={minStay}
                    onChange={(event) => setMinStay(Number(event.target.value))}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-inner outline-none transition focus:border-indigo-400"
                    required
                  />
                </label>
                <label className="space-y-1 text-sm font-semibold text-slate-700">
                  <span>depth</span>
                  <select
                    value={depth}
                    onChange={(event) => setDepth(Number(event.target.value))}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-inner outline-none transition focus:border-indigo-400"
                  >
                    {DEPTH_OPTIONS.map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="space-y-1 text-sm font-semibold text-slate-700">
                  <span>Частота парсинга</span>
                  <select
                    value={interval}
                    onChange={(event) => setInterval(Number(event.target.value))}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-inner outline-none transition focus:border-indigo-400"
                  >
                    {INTERVAL_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="space-y-1 text-sm font-semibold text-slate-700">
                  <span>guests</span>
                  <select
                    value={guests}
                    onChange={(event) => setGuests(Number(event.target.value))}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-inner outline-none transition focus:border-indigo-400"
                  >
                    {GUEST_OPTIONS.map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
                <input
                  type="checkbox"
                  checked={startNow}
                  onChange={(event) => setStartNow(event.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-indigo-600"
                />
                Запустить парсинг сейчас
              </label>

              {formError && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  {formError}
                </div>
              )}

              <div className="flex flex-wrap justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setFormOpen(false);
                    resetForm();
                  }}
                  disabled={saving}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-60"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={saving || !canEdit}
                  className="inline-flex items-center gap-2 rounded-2xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saving && <Loader2 size={16} className="animate-spin" />}
                  Сохранить
                </button>
              </div>
            </form>
          )}

          {parsing.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-slate-200 bg-white px-5 py-8 text-center text-sm text-slate-500">
              Парсинги не настроены
            </div>
          ) : (
            <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm text-slate-600">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-400">
                    <tr>
                      <th className="px-4 py-3">min_stay</th>
                      <th className="px-4 py-3">depth</th>
                      <th className="px-4 py-3">Частота парсинга</th>
                      <th className="px-4 py-3">guests</th>
                      <th className="px-4 py-3">last_date</th>
                      <th className="px-4 py-3 text-right">Удаление</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {parsing.map((task, index) => (
                      <tr key={getTaskKey(task, index)}>
                        <td className="px-4 py-3 font-medium text-slate-800">{task.min_stay}</td>
                        <td className="px-4 py-3">{task.depth}</td>
                        <td className="px-4 py-3">{formatInterval(task.interval)}</td>
                        <td className="px-4 py-3">{task.guests}</td>
                        <td className="px-4 py-3">{formatDate(task.last_date)}</td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end">
                            <button
                              type="button"
                              onClick={() => setDeletingTask(task)}
                              disabled={!task.task_id || !canEdit}
                              aria-label="Удалить парсинг"
                              title={!task.task_id ? "Не задан task_id" : "Удалить парсинг"}
                              className="inline-flex items-center justify-center rounded-2xl border border-red-200 bg-red-50 p-2 text-red-600 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </Modal>

      <ConfirmDeleteModal
        open={Boolean(deletingTask)}
        onClose={() => setDeletingTask(null)}
        onConfirm={handleDeleteConfirm}
        title="Удаление парсинга"
        message="Вы уверены, что хотите удалить этот парсинг?"
        confirmLabel="Удалить"
        loadingLabel="Удаляем..."
        errorMessage="Ошибка удаления парсинга"
      />
    </>
  );
}
